import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImportTool } from "../types/import-tool";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Card, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Search } from "lucide-react";

import { NodeDefinitionService } from "@/features/Workflows/services/NodeDefinitionService";

interface ImportToolSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (tool: ImportTool) => void;
  categoryFilter?: string; 
}

// Ordem preferida das categorias
const CATEGORY_ORDER = ['Files', 'Triggers', 'API', 'Databases', 'Embeddings'];

/**
 * Normaliza um item de `GET /node-definitions` para `ImportTool`.
 *
 * O serviço devolve `Record<string, unknown>[]`, e a lista era apenas *casteada*
 * para `ImportTool[]`. O tipo promete `name` e `description` como string, mas
 * nada garantia isso em runtime — e a busca chama `.toLowerCase()` nos dois, o
 * que quebra a gaveta inteira se um vier nulo. Item sem `id` ou `name` é
 * descartado; `description` ausente vira string vazia, que é como a busca e o
 * card já a tratam.
 */
function toImportTool(raw: Record<string, unknown>): ImportTool | null {
  const id = typeof raw.id === 'string' ? raw.id : null;
  const name = typeof raw.name === 'string' ? raw.name : null;
  if (!id || !name) return null;

  return {
    ...(raw as unknown as ImportTool),
    id,
    name,
    description: typeof raw.description === 'string' ? raw.description : '',
    category: typeof raw.category === 'string' ? raw.category : '',
    type: typeof raw.type === 'string' ? raw.type : '',
  };
}

export function ImportToolSelector({ open, onClose, onSelect, categoryFilter }: ImportToolSelectorProps) {
  const { t } = useTranslation();

  const [tools, setTools] = useState<ImportTool[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Sem isto a falha do fetch ficava indistinguível de "não há ferramentas".
  const [loadFailed, setLoadFailed] = useState(false);

  // Sequência da requisição: trocar de categoria rápido dispara buscas
  // concorrentes, e a que responde por último não é necessariamente a última
  // pedida. Só a mais recente escreve no estado.
  const latestRequestRef = useRef(0);

  const fetchTools = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const { data, error } = await NodeDefinitionService.getAll(undefined, undefined, undefined, 1, 100);
      if (error) throw error;

      // Filtrar as ferramentas por categoria no frontend
      const allTools = (data || [])
        .map(toImportTool)
        .filter((tool): tool is ImportTool => tool !== null);
      
      // Se categoryFilter estiver definido, filtra apenas por essa categoria
      const filteredTools = categoryFilter 
        ? allTools.filter((tool: ImportTool) => tool.category === categoryFilter)
        : allTools.filter((tool: ImportTool) => CATEGORY_ORDER.includes(tool.category));
      
      // Ordenar as ferramentas por categoria e nome
      filteredTools.sort((a: ImportTool, b: ImportTool) => {
        // Se houver um filtro de categoria, ordena apenas por nome
        if (categoryFilter) {
          return a.name.localeCompare(b.name);
        }
        
        const categoryIndexA = CATEGORY_ORDER.indexOf(a.category);
        const categoryIndexB = CATEGORY_ORDER.indexOf(b.category);
        
        if (categoryIndexA !== categoryIndexB) {
          return categoryIndexA - categoryIndexB;
        }
        
        return a.name.localeCompare(b.name);
      });
      
      if (latestRequestRef.current !== requestId) return;
      setTools(filteredTools);
    } catch (error) {
      if (latestRequestRef.current !== requestId) return;
      console.error("Error in fetchTools:", error);
      // Limpa a lista junto: a mensagem de erro só é renderizada quando não há
      // resultado, e sem isto a falha ficava escondida atrás dos dados antigos.
      setTools([]);
      setLoadFailed(true);
    } finally {
      if (latestRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [categoryFilter]);

  // `categoryFilter` entra nas dependências: o fetch filtra por ele, e sem isto
  // trocar a categoria com a gaveta aberta mantinha a lista anterior na tela.
  useEffect(() => {
    if (open) {
      fetchTools();
    }
  }, [open, fetchTools]);

  const filteredTools = tools.filter(tool =>
    tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Agrupa as ferramentas por categoria
  const groupedTools = filteredTools.reduce((acc, tool) => {
    const category = tool.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(tool);
    return acc;
  }, {} as Record<string, ImportTool[]>);

  // Define as categorias a serem exibidas
  const categoriesToShow = categoryFilter ? [categoryFilter] : CATEGORY_ORDER;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>{t("common.pages.knowledge.imports.selectTool")}</SheetTitle>
        </SheetHeader>

        <div className="relative mt-4">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
          <Input
            placeholder={t("common.pages.knowledge.imports.searchTools")}
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <ScrollArea className="mt-6 h-[calc(100vh-8rem)]">
          {categoriesToShow.map(category => {
            const toolsInCategory = groupedTools[category];
            if (!toolsInCategory?.length) return null;

            return (
              <div key={category} className="mb-6">
                <h3 className="mb-3 px-2 text-lg font-semibold">
                  {t(`common.categories.${category}`, { defaultValue: category })}
                </h3>
                <div className="space-y-2">
                  {toolsInCategory.map((tool: ImportTool) => (
                    <Card
                      key={tool.id}
                      className="cursor-pointer transition-colors hover:bg-muted"
                      onClick={() => onSelect(tool)}
                    >
                      <CardHeader className="flex flex-row items-center gap-4 space-y-0 p-4">
                        <img
                          src={tool.logo_url}
                          alt={tool.name}
                          className="h-10 w-10 object-contain"
                        />
                        <div>
                          <CardTitle className="text-base">{tool.name}</CardTitle>
                          <CardDescription className="text-sm">
                            {tool.description}
                          </CardDescription>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
          {filteredTools.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              {isLoading
                ? t('common.messages.loading')
                : loadFailed
                  ? t('common.messages.errorDescription')
                  : t('common.messages.noResults')}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
